// Org switcher. The intranet is an org-level product (MODEL.md inv 15):
// each team-org has one "home" workspace (`type:'shared'`, aka the HQ
// workspace) that backs chat, tickets, docs, meet, KB, etc. Workspaces
// exist for billing boundaries in the editor — not for collab scoping.
//
// This component lets the user pick which org's intranet to view — now a
// 2-level slider (orgs → that org's workspaces; the switch commits when a
// workspace is picked). See AppOrgSwitcherMenu + AppWsGroup / AppWsItem +
// _slideOrgSwitcher below.
// Activation goes through `el.sdk.switchWorkspace(homeWsId)` — the SDK
// owns the federation/refresh round-trip; the frontend never reaches
// directly into sdk-bridge. RLS picks up the new active_workspace_id
// from the refreshed JWT and re-scopes every tenant-table query.
//
// Renders whenever the user belongs to at least one team org. A "+ Create
// organization" footer button flips `root._createOrgModalOpen = true`,
// which opens `AppCreateOrgModal` (root-mounted sibling component, see
// `workspace/components/AppCreateOrgModal.js`). This used to be a Link to
// `canvasUrl('/organizations/new')` — but `canvas.<tier>.symbols.app` is
// an NXDOMAIN host and there is no `/organizations/new` route on canvas
// either, so the link landed on Chrome's "Site can't be reached" page
// (bug #2186). Keeping the create flow in-shell also avoids losing the
// user's current workspace context.
//
// Previously named `AppWorkspaceSwitcher` and lived under
// `workspace/components/`. Renamed + relocated to `shared/components/`
// so any submodule (canvas / chat / tickets / …) can mount it via bare-
// key reference without depending on the workspace shell directly.

import { getActiveWorkspacePointer } from '../functions/activeWorkspacePointer.js'
import { visibleOrgs } from '../functions/visibleOrgs.js'
import { userFacingErrorMessage } from '../functions/userFacingError.js'
import { workspaceOrigin } from '../functions/canvasUrl.js'
import { deploymentChannel } from '../functions/workspaceApiBase.js'
import { shellUrl as channelShellUrl } from '@symbo.ls/channels'
import { ORG_SWITCHER_DROPDOWN_ID } from '../functions/orgSwitcherDropdown.js'
import { touchRow } from './_touchTarget.js'
import {
  hasWorkspacePrefix,
  stripWorkspacePrefix,
  withWorkspacePrefix
} from '../functions/workspacePrefix.js'

// Gate-A capstone (Mongo-only since 2026-07-31): the org/workspace IDENTITY
// reads below source from Mongo state on root (root.orgs / root.orgWorkspaces /
// root.org / the active-workspace pointer), never the retired governance JWT's
// app_metadata (root.jwtClaims). Was flag-gated behind `__USE_MONGO_CLAIMS__`
// during the Gate A cutover (2026-07-03); collapsed after the server stopped
// minting `app_metadata` claims on 2026-07-28, which made the flag's OFF
// (jwtClaims + localStorage fallback) branch permanently dead.

// The active org's Mongo id — root.org is the active-org OrgMember row populated
// by subscribeAuthSession / the eager /auth/me scope fetch (bootShell). This is
// the Mongo equivalent of the governance JWT's `workspace_org_id` claim. Used by
// the ON path of every `workspace_org_id` read so the active-org id no longer
// depends on the (stale-prone) governance claim.
const _mongoActiveOrgId = (root) => {
  const org = root && root.org
  return (org && (org.id || org._id)) || ''
}

// Workspace-switch guard timer: cleared by app.js's onWorkspaceChange when the
// claims-change path fires successfully. If it doesn't fire within 5 s the
// timer falls back to a full reload so the overlay never hangs.
//
// FA201 / frank trap (WS-SHARED-MODULE-LET-BLOCKS-EVERY-PUSH-1) — the handle
// used to be a module-scope `let _wsFallbackTimer = null`, read by the
// EXPORTED `clearWorkspaceSwitchFallback` below. frank hoists that export into
// the emitted `globalScope` and revives it with a bare indirect eval, where
// nothing module-level is in scope, so the read emitted a guaranteed
// ReferenceError and frank's emission gate refused the whole publish
// (`PUBLISH BLOCKED — [unresolvable-identifier]`) for EVERY project that
// inlines this package.
//
// Re-homed onto `globalThis.__wsSwitchFallbackState`, per the
// pointerCapability.js template. This is STRICTLY safer than the closure it
// replaces: `clearWorkspaceSwitchFallback` is imported and called
// bare/argument-less by `packages/workspace/functions/loadPrivateData.js` (a
// DIFFERENT package), and a bundler that gives the two packages separate
// module instances would have given them two separate timers. One global bag
// is one timer, whatever the bundler does. `globalThis` rather than `window`
// because it is defined in every runtime this package loads in; in a browser
// it IS `window`. The lazy create is INLINED at each site on purpose — a
// shared module-scope getter would just relocate the same
// bare-sibling-call hazard this fix removes.

// Called by app.js's _onWorkspaceChange after the in-place data refresh
// completes. Prevents the 5 s fallback from triggering a redundant reload.
export const clearWorkspaceSwitchFallback = () => {
  if (!globalThis.__wsSwitchFallbackState) globalThis.__wsSwitchFallbackState = { timer: null }
  const state = globalThis.__wsSwitchFallbackState
  if (state.timer) {
    clearTimeout(state.timer)
    state.timer = null
  }
}

// Loader-state pair. `_wsSwitching` drives the AppOrgSwitchOverlay
// (a reactive guard — see _isLoaderActive in workspace/app.js, NOT
// applyLoader, which would also flip `root.loading` and trip studios.js's
// "loading nodes" indicator). `_pendingActiveOrgId` is the optimistic pick
// _activeOrgModuleInternal reads until the next /auth/me payload lands. Both flags are
// raised pre-switch and cleared on every exit path (success, error, 400 ms
// non-federated tick) so they always travel together.
// `_startSwitching` no longer toggles `_wsMenuOpen` — the dropdown
// plugin owns visibility, and callers explicitly dispatch
// `closeDropdown('appOrgSwitcher')` after firing this. Keeps the state
// transition atomic: pending id + loader label flip together.
// The MainLoader is shown directly (without touching `root.loading`) so
// studios.js's "loading nodes" indicator is unaffected — `root.loading`
// stays unchanged. `_isLoaderActive` in app.js already gates MainLoader
// display on `_wsSwitching`; the explicit update here triggers the
// `scaleOutIn` spiral so the switch shows the full boot-loader experience.
const _startSwitching = (root, label, orgId) => {
  if (!root) return
  root.update({
    _pendingActiveOrgId: orgId,
    _wsSwitching: label || 'workspace'
  })
  // Show the boot-screen MainLoader spiral directly — same as app boot.
  // Guard for SSR / environments where MainLoader hasn't mounted yet.
  if (root.MainLoader && root.MainLoader.update) {
    root.MainLoader.update({ animation: 'scaleOutIn', animationDelay: '0' })
  }
}

const _endSwitching = (root, extra) => {
  if (!root) return
  root.update({ _wsSwitching: null, _pendingActiveOrgId: null, ...(extra || {}) })
  // Hide the MainLoader that _startSwitching raised. Clearing `_wsSwitching`
  // already removes it reactively (display:'none' via _isLoaderActive in
  // app.js), but firing scaleInOut here ensures the retract animation plays
  // on the synchronous exit paths (same-org non-federated success + all error
  // paths) exactly as it does on the async onWorkspaceChange completion path.
  if (root.MainLoader && root.MainLoader.update) {
    root.MainLoader.update({ animation: 'scaleInOut', animationDelay: 'F' })
  }
}

// The user's full org membership, BEFORE the _invalidOrgs permissions filter.
// Mongo only — root.orgs (populated by subscribeAuthSession from /auth/me) is
// the canonical org list; the retired governance JWT's app_metadata.orgs
// narrowed to team-only orgs and is no longer read.
//
// The list the switcher OFFERS: a team member's own EMPTY personal org
// (/auth/me `hasContent: false`, the background org every account owns) is
// left out (POB round 5, Nika 2026-10-31 — functions/visibleOrgs.js). The org
// the person is IN, or an in-tab switch is moving to, is never left out.
const _rawOrgs = (root) => {
  // Mongo only — root.orgs is the canonical org list (/auth/me).
  const fromState = Array.isArray(root?.orgs) ? root.orgs : null
  return visibleOrgs(fromState || [], {
    keep: [root?.org?.id, root?.org?._id, root?.org?.slug, root?._pendingActiveOrgId]
  })
}

// The membership list from /auth/me is ONE PAGE (20 rows on an account with
// hundreds of orgs), and the active org can sit outside it. root.org is the
// resolved active-org row (id / slug / name / role), so it leads the list when
// the page does not carry it. Without this the Trigger read a bare
// "Organization" and the list showed no active row (nikoloza/local, 475 orgs,
// 2026-09-24 per Nika "there is no active organization (in top right) fix it").
const _withActiveOrg = (root, raw) => {
  const active = root?.org
  const id = active && (active.id || active._id)
  if (!id || !(active.name || active.displayName)) return raw
  if (raw.some((o) => o.id === id)) return raw
  return [{ ...active, id, name: active.name || active.displayName }, ...raw]
}

const _orgs = (root) => {
  const raw = _withActiveOrg(root, _rawOrgs(root))
  if (!raw.length) return []
  // Filter out orgs the server rejected with 403 earlier this session
  // (see _commitOrgSwitch's catch). Reading root._invalidOrgs makes this
  // factory reactive — DOMQL re-renders the org list when a failed switch
  // flushes a stale id into the set.
  const invalid = root?._invalidOrgs
  if (Array.isArray(invalid) && invalid.length) {
    const skip = new Set(invalid)
    const filtered = raw.filter((o) => !skip.has(o.id))
    // Ticket 6a34260c — never let the permissions filter EMPTY the list: if
    // every org the user has is currently flagged invalid (e.g. a transient
    // 403 storm, or a single-org user who hit a permissions error switching to
    // their only org), returning [] would collapse the whole switcher via the
    // `hide` gate and strand the user with no way to switch back. Fall back to
    // the raw list so the selector stays usable; the flagged orgs simply
    // re-surface (a retry is better than a vanished control).
    return filtered.length ? filtered : raw
  }
  return raw
}

const _workspaces = (root) => {
  // Mongo only — flatten root.orgWorkspaces (the per-org lists fetched via
  // sdk.execute('workspace','list',{organization}), already normalized to
  // id/name/org_id). Used by _orgWorkspaces' fallback + _activeOrgModuleInternal's
  // workspace→org lookup; both also consult root.orgWorkspaces directly, so
  // this stays self-consistent.
  const byOrg = root && root.orgWorkspaces
  if (!byOrg || typeof byOrg !== 'object') return []
  const out = []
  for (const k of Object.keys(byOrg)) {
    const list = byOrg[k]
    if (Array.isArray(list)) out.push(...list)
  }
  return out
}

// Workspaces for one org. Prefers the per-org list fetched via
// sdk.execute('workspace','list',{organization}) and seeded into
// root.orgWorkspaces by the Trigger BEFORE the dropdown opens — those items'
// `organization` field is in the SAME id-space as root.orgs[].id. Falls back
// to filtering the flattened root.orgWorkspaces (_workspaces) only when the
// per-org fetch hasn't run or failed for that org.
const _orgWorkspaces = (orgId, root) => {
  const fetched = root && root.orgWorkspaces && root.orgWorkspaces[orgId]
  const base = Array.isArray(fetched)
    ? fetched
    : _workspaces(root).filter((w) => (w.org_id || w.organization) === orgId)
  // Remove workspaces a switch has proven unswitchable this session (a
  // broken/orphaned federation, or one the user can't actually reach) so the
  // dropdown stops offering a row that only ever errors with "Couldn't switch".
  // Mirrors the org-level `_invalidOrgs` filter in `_orgs`. Reading
  // root._invalidWorkspaces makes this factory reactive — the L2 list re-renders
  // when a failed switch flushes an id into the set.
  const invalid = root?._invalidWorkspaces
  if (Array.isArray(invalid) && invalid.length) {
    const skip = new Set(invalid)
    const filtered = base.filter((w) => !skip.has(w.id || w.workspace_id || w._id))
    // Never let the filter EMPTY an org's list (same safeguard as _orgs): if every
    // workspace is flagged, show the raw list so the user isn't stranded with no
    // way in — a retry beats a vanished org.
    return filtered.length ? filtered : base
  }
  return base
}

// The JWT's `active_workspace_id` claim can go stale — it isn't always re-minted
// in lock-step with a workspace switch (it can point at a workspace from another
// org entirely). Prefer the TAB'S OWN resolved workspace (root.workspace.id —
// matches loadPrivateData.js / pages/main.js's TAB-TRUTH-FIRST precedence)
// first, then the SDK's persisted active workspace
// (localStorage.activeWorkspace, its documented cross-tab contract — see
// workspace/wireGlobalListeners.js + sdk switchWorkspace) as a fallback for
// before root.workspace has landed. Keeps the active-workspace checkmark
// honest for THIS tab, not whichever tab last wrote the shared pointer
// (tickets/workspace.md P1 tenant-isolation audit, 2026-08-08 — with MWT
// off, a sibling tab's switch previously could flip which workspace this
// tab's switcher showed as "active").
const _activeWsId = (root) => {
  if (root?.workspace?.id) return String(root.workspace.id)
  try {
    // No window gate: getActiveWorkspacePointer reads globalThis.sessionStorage /
    // globalThis.localStorage (per-tab MWT pointer) and null-safes both — the old
    // `window.localStorage` guard checked a storage the pointer never reads.
    const ls = getActiveWorkspacePointer()
    if (ls) return ls
  } catch (err) {
    console.warn('[AppOrgSwitcher] read activeWorkspace failed:', err?.message || err)
  }
  // Mongo-native only — localStorage.activeWorkspace IS the Mongo-native
  // active-workspace pointer (written by sdk.switchWorkspace / PATCH
  // /auth/me/active-org; bootShell fetches root.workspace from it). No
  // fallback to the retired governance JWT's stale-prone active_workspace_id.
  return ''
}

// Find the home workspace for an org. Preference order:
//   1. type === 'shared' (server convention for team/HQ workspaces)
//   2. type === 'hq' (reserved if/when server adds it)
//   3. first workspace in that org by claim order
const _homeWorkspace = (org, root) => {
  const wss = _orgWorkspaces(org.id, root)
  return wss.find((w) => w.type === 'hq') || wss.find((w) => w.type === 'shared') || wss[0] || null
}

// Returns the org the UI should treat as active. Resolution order:
//   1. `_pendingActiveOrgId` — optimistic pick set THIS tick by the user
//      while `PATCH /auth/me/active-org` is in flight.
//   2. `root.org.id` — the resolved active-org OrgMember row, populated by
//      `subscribeAuthSession` from `/auth/me` (`user.activeOrganization`).
//      Survives reloads (it's in Mongo) and is replicated across every
//      device via the socket.io `active-org-changed` event.
//   3. First org in the membership list — last-resort default for fresh users.
const _activeOrgModuleInternal = (root) => {
  const orgs = _orgs(root)
  const claimOrgId = _mongoActiveOrgId(root)
  if (!orgs.length) {
    // Ticket 6a34260c — the org list can be transiently empty after a
    // permissions error / claims re-mint. Keep the Trigger meaningful by
    // synthesizing a minimal active-org record from the surviving server
    // context (Mongo `org` state, then the active-org claim) so the switcher
    // still shows WHERE the user is instead of a bare "Organization".
    const stateOrg = root?.org
    if (stateOrg && (stateOrg.id || stateOrg._id)) {
      return { id: stateOrg.id || stateOrg._id, name: stateOrg.name || stateOrg.displayName }
    }
    if (claimOrgId) return { id: claimOrgId, name: root?.org?.name || '' }
    return null
  }
  const pendingId = root?._pendingActiveOrgId
  if (pendingId) {
    const pending = orgs.find((o) => o.id === pendingId)
    if (pending) return pending
  }
  if (claimOrgId) {
    const fromClaim = orgs.find((o) => o.id === claimOrgId)
    if (fromClaim) return fromClaim
  }
  const activeId = _activeWsId(root)
  const w = _workspaces(root).find((x) => (x.id || x.workspace_id || x._id) === activeId)
  if (!w) return orgs[0] || null
  return orgs.find((o) => o.id === (w.org_id || w.organization)) || null
}

// ORG-URL-1: decides whether `_commitOrgSwitch`'s early, OPTIMISTIC `/w/…`
// URL rewrite should fire. That rewrite races the shell's atomic
// `sdk.orgSwitched` listener (workspace/app.js `_handleOrgSwitched` ->
// `syncWorkspaceUrl(..., {rewriteOrg:true})`), which resolves the full
// target path from SETTLED post-switch state (org + the resolved workspace +
// its workspace list). The optimistic version strips the path's second
// segment by checking it against the CURRENT (pre-switch, OLD) org's
// workspace list via `stripWorkspacePrefix` — a segment the resolver doesn't
// recognize (e.g. a raw `ws_<id>` handle) is left glued onto the rest of the
// path, and `withWorkspacePrefix(org.slug, rest)` then glues the NEW org
// slug onto that stale leftover: `/w/system/ws_OXN_VBk3Vx` (org updated,
// workspace id still the OLD org's). `_commitOrgSwitch` is only ever called
// with a known `homeWorkspaceId` (AppWsItem.onClick, the sole call site), so
// the optimistic rewrite is skipped whenever one is present — the atomic
// listener owns the URL for that case and is independently correct.
const _allowOptimisticUrlRewrite = (pathname, homeWorkspaceId) =>
  !homeWorkspaceId && hasWorkspacePrefix(pathname)

// The Gate-A identity-read resolvers and the switch commit, as one object.
// These are the module-private read-helpers every org/workspace identity read
// funnels through; exporting them (rather than the component objects) lets
// the offline unit test assert the Mongo-sourced resolution per read without
// a DOM/render. Two shell readers reach it through the component registry
// (`components.__orgSwitcherInternals`), never a static import:
//   - workspace/app.js reads `activeOrg` (the shell title);
//   - the CLI mode's workspace picker (workspace functions/agentModeRuntime.js,
//     host.switchWorkspace) commits a pick through `commitOrgSwitch`, so a pick
//     in another org takes the header's own path (switchOrg, loader, deadline,
//     URL, failure toast). Keep its signature: (el, root, org, workspaceId, label).
export const __orgSwitcherInternals = {
  rawOrgs: _rawOrgs,
  orgs: _orgs,
  workspaces: _workspaces,
  orgWorkspaces: _orgWorkspaces,
  activeWsId: _activeWsId,
  activeOrg: _activeOrgModuleInternal,
  mongoActiveOrgId: _mongoActiveOrgId,
  allowOptimisticUrlRewrite: _allowOptimisticUrlRewrite,
  // the switch commit itself: the deadline tests (AppOrgSwitcher.test.js)
  // and the CLI mode's workspace picker (see the header above)
  commitOrgSwitch: (...a) => _commitOrgSwitch(...a)
}

export const AppOrgSwitcher = {
  // FA204 / frank trap — every helper below lives in `scope`, not module
  // scope: a bare module-scope OR sibling-exported read silently reads as
  // undefined once the component is frank.toJSON'd for platform delivery
  // (workspace d6ea1142, project_frank_drops_module_local_helpers). The
  // identity-read cluster (`rawOrgs`/`orgs`/`workspaces`/`activeWsId`/
  // `activeOrg`/`mongoActiveOrgId`) is DUPLICATED here rather than shared
  // with the module-scope originals those functions still exist at module
  // scope too, ONLY because `__orgSwitcherInternals` (below) exports them
  // BARE for `AppOrgSwitcher.test.js`'s offline Node coverage, and
  // `AppOrgSwitcher.hide` itself is called directly with `el: null` by that
  // same test — so `hide` keeps a dual-mode read (`el?.scope?.rawOrgs` in
  // the live tree, falling back to the module-scope `_rawOrgs` when `el` is
  // null). Every OTHER factory below is scope-only.
  scope: {
    mongoActiveOrgId(root) {
      const org = root && root.org
      return (org && (org.id || org._id)) || ''
    },
    rawOrgs(root) {
      // the switcher's list: functions/visibleOrgs.js (see `_rawOrgs`)
      const fromState = Array.isArray(root?.orgs) ? root.orgs : null
      return visibleOrgs(fromState || [], {
        keep: [root?.org?.id, root?.org?._id, root?.org?.slug, root?._pendingActiveOrgId]
      })
    },
    // Scope copy of _withActiveOrg (FA204: a scope method never reads module
    // scope): the active org leads the page when the page does not carry it.
    withActiveOrg(root, raw) {
      const active = root?.org
      const id = active && (active.id || active._id)
      if (!id || !(active.name || active.displayName)) return raw
      if (raw.some((o) => o.id === id)) return raw
      return [{ ...active, id, name: active.name || active.displayName }, ...raw]
    },
    orgs(root) {
      const raw = this.withActiveOrg(root, this.rawOrgs(root))
      if (!raw.length) return []
      const invalid = root?._invalidOrgs
      if (Array.isArray(invalid) && invalid.length) {
        const skip = new Set(invalid)
        const filtered = raw.filter((o) => !skip.has(o.id))
        return filtered.length ? filtered : raw
      }
      return raw
    },
    workspaces(root) {
      const byOrg = root && root.orgWorkspaces
      if (!byOrg || typeof byOrg !== 'object') return []
      const out = []
      for (const k of Object.keys(byOrg)) {
        const list = byOrg[k]
        if (Array.isArray(list)) out.push(...list)
      }
      return out
    },
    activeWsId(root) {
      if (root?.workspace?.id) return String(root.workspace.id)
      try {
        const ls = getActiveWorkspacePointer()
        if (ls) return ls
      } catch (err) {
        console.warn('[AppOrgSwitcher] read activeWorkspace failed:', err?.message || err)
      }
      return ''
    },
    activeOrg(root) {
      const orgs = this.orgs(root)
      const claimOrgId = this.mongoActiveOrgId(root)
      if (!orgs.length) {
        const stateOrg = root?.org
        if (stateOrg && (stateOrg.id || stateOrg._id)) {
          return { id: stateOrg.id || stateOrg._id, name: stateOrg.name || stateOrg.displayName }
        }
        if (claimOrgId) return { id: claimOrgId, name: root?.org?.name || '' }
        return null
      }
      const pendingId = root?._pendingActiveOrgId
      if (pendingId) {
        const pending = orgs.find((o) => o.id === pendingId)
        if (pending) return pending
      }
      if (claimOrgId) {
        const fromClaim = orgs.find((o) => o.id === claimOrgId)
        if (fromClaim) return fromClaim
      }
      const activeId = this.activeWsId(root)
      const w = this.workspaces(root).find((x) => (x.id || x.workspace_id || x._id) === activeId)
      if (!w) return orgs[0] || null
      return orgs.find((o) => o.id === (w.org_id || w.organization)) || null
    },

    // Every-org workspace prefetch state — private to this component (the
    // Trigger + onCreate poll are the only callers), so it's a plain scope
    // data property rather than a cross-component `el.context` bag (unlike
    // AppNavbar's more-dropdown coordination, nothing outside AppOrgSwitcher
    // needs to see this).
    orgWsInFlight: new Set(),
    // Fetch orgs' workspaces via the SDK and seed root.orgWorkspaces. Each
    // item's `organization` field is in the SAME id-space as root.orgs[].id
    // (unlike the JWT claim's workspace.org_id), so the switcher's L2 lists
    // become correct. NON-BLOCKING + per-org single-flight: callers never
    // await it, so opening the menu is always instant; a per-org failure is
    // skipped (that org keeps the claim fallback).
    //
    // SCOPE discipline (boot F6 — tickets/workspace.md BOOT-RESHUFFLE root
    // cause 6): this must NEVER fan out to every org at boot. The old
    // mount-time prefetch fired one `GET /core/workspaces?organization=<id>`
    // per org — 13 simultaneous calls for a 13-org user, saturating the
    // browser's 6-connection HTTP/1.1 pool at the exact moment boot data
    // loads (measured 1.7-3.3s per call, 9-12s total boot data). So: mount
    // warms ONLY the active org (one call, off the critical path); the FULL
    // fan-out runs when the menu actually opens (Trigger.onClick), which is
    // also when the data is actually needed — and re-runs on each open so
    // newly-created workspaces show up. `onlyOrgs` narrows the fetch to a
    // subset; omitted = all orgs (the menu-open refresh). The in-flight set
    // is per-org so a pending active-org warm never blocks the menu-open
    // fan-out from covering the other orgs.
    // `displayName` FIRST. The legacy `name` field is frozen at "Default" on
    // older workspace records, so reading it first hid every rename — 8 of 23
    // workspaces printed "Default" here on 2026-09-04 while their real name sat
    // in `displayName`. THE rule, and the measurement, live on
    // `workspaceDisplayName` in functions/activeWorkspaceRecord.js. This is a
    // scope copy for the same frank reason as the identity-read cluster above:
    // a scope method has no `el`, so it cannot reach the barrel through
    // `el.call`. Keep the two in step.
    wsDisplayName(w) {
      return (w && (w.displayName || w.name)) || ''
    },

    prefetchOrgWorkspaces(root, sdk, onlyOrgs) {
      if (!root || !sdk || !sdk.execute) return
      const wanted = Array.isArray(onlyOrgs) && onlyOrgs.length ? onlyOrgs : this.orgs(root)
      const inFlight = this.orgWsInFlight
      const orgs = wanted.filter((org) => org && org.id && !inFlight.has(org.id))
      if (!orgs.length) return
      for (const org of orgs) inFlight.add(org.id)
      const done = () => {
        for (const org of orgs) inFlight.delete(org.id)
      }
      Promise.all(
        orgs.map((org) =>
          sdk
            .execute('workspace', 'list', { organization: org.id })
            .then((r) => (r && r.items) || [])
            .catch(() => null)
        )
      ).then(
        (lists) => {
          const orgWorkspaces = { ...(root.orgWorkspaces || {}) }
          orgs.forEach((org, i) => {
            // Normalize the SDK Workspace shape (_id / displayName /
            // organization) to the fields the rows + switch read (id / name /
            // org_id) so AppWsItem renders names + the active check/switch
            // use the right id.
            if (Array.isArray(lists[i]))
              orgWorkspaces[org.id] = lists[i].map((w) => ({
                ...w,
                id: w.id || w._id,
                name: this.wsDisplayName(w),
                org_id: w.org_id || w.organization || org.id
              }))
          })
          root.update({ orgWorkspaces })
          done()
        },
        (err) => {
          console.warn('[AppOrgSwitcher] workspace prefetch failed:', (err && err.message) || err)
          done()
        }
      )
    },

    // Open the org-switcher menu INSTANTLY. THIS is where the every-org
    // workspace prefetch runs (boot F6 — mount only warms the active org;
    // see onCreate): fired in the BACKGROUND (never awaited) so a slow SDK
    // call can't delay or block the menu from opening, and re-fired on each
    // open so newly-created workspaces show up. The active org's list is
    // already warm, and the claim fallback covers a drill that beats the
    // fetch.
    // ORGSWITCHER-PANEL-SURFACE-1 (2026-09-02 per Nika: "fix why orgswitcher
    // has its own background — remove stale bg and rely on original dropdown
    // background — it also has empty space at the right").
    //
    // BOTH symptoms were one defect: this call used to pin the plugin Panel to
    // a RAW `minWidth: '320px'` while the body (AppOrgSwitcherMenu) sizes
    // itself with the em-based `width: 'G'` token — 305px at the header's 17px
    // font. A raw px shell can never track an em body, so the Panel stood 15px
    // wider than the card inside it and the Panel's OWN surface (the dropdown
    // plugin paints `theme` + `round` + `shadow` on its shell) showed as a
    // darker strip down the RIGHT edge — the "stale background", and the
    // "empty space", in one measurement.
    //
    // The fix is to declare NO width here at all: the body owns its width
    // (content width with a token minimum), so the Panel shrink-wraps to it and
    // exactly ONE surface — the house MenuPanel card every other dropdown in
    // the product shows — is visible. `round` is handed through so the shell's
    // corners match the body's `radiusCard` instead of peeking past them at
    // the four corners (same call shape AiSettingsButton documents for the AI
    // menus: geometry declared in TOKENS, panel and body agreeing).
    // Beside a navbar RAIL, not under it: the switcher sits at the foot of a
    // left or right rail, so its menus open towards the page from the rail's
    // outer edge, the menu's foot on the trigger's — every navbar menu's one
    // anchor (functions/navbarLayout.js navbarMenuAnchor). At the top and the
    // bottom the trigger anchors as ever.
    openOrgSwitcherMenu(e, el, s) {
      this.prefetchOrgWorkspaces(s && s.root, el.sdk)
      el.call('openDropdown', {
        ...el.call('navbarMenuAnchor', 'bottom-start'),
        id: ORG_SWITCHER_DROPDOWN_ID,
        round: 'radiusCard',
        content: { extends: 'AppOrgSwitcherMenu' }
      })
    },

    openInviteDropdown(e, el, s) {
      // Opens AT ONCE, in the click (the same call shape as
      // openOrgSwitcherMenu above). The invite panel loads its own member
      // list and shows a loading state while it does (MembersInInvite) — it
      // used to wait here for a listOrgMembers round trip with no feedback,
      // then anchor from a rect snapshotted before the wait.
      el.call('openDropdown', {
        ...el.call('navbarMenuAnchor', 'bottom-end'),
        id: 'appInvite',
        // The body (InviteDropdown) extends MenuPanel and carries
        // `radiusCard`, so the plugin shell takes that radius too and never
        // shows its own corners behind the card.
        round: 'radiusCard',
        content: { extends: 'InviteDropdown' }
      })
    }
  },

  // Render whenever the user has ANY org context — the raw membership list, an
  // active-org claim, or a resolved active org. Ticket 6a34260c: gating on the
  // _invalidOrgs-FILTERED `_orgs` meant a permissions error (a 403 that flagged
  // the user's only/all orgs, or a re-mint that briefly emptied root.orgs) made
  // the whole selector VANISH, stranding the user. Gate on the unfiltered
  // context instead so the control survives transient errors; `_orgs`'s own
  // fallback keeps the dropdown list populated.
  hide: (el, s) => {
    const rawOrgs = el?.scope?.rawOrgs ? (r) => el.scope.rawOrgs(r) : _rawOrgs
    const root = s.root
    if (rawOrgs(root).length) return false
    if (root?.org && (root.org.id || root.org._id)) return false
    return true
  },

  position: 'relative',
  flow: 'x',
  align: 'center left',

  // Warm ONLY the active org's workspace list shortly after mount (deferred
  // 800ms so it stays off the critical boot path), retrying until orgs load.
  // The full every-org prefetch moved to menu-open (Trigger.onClick) — boot F6:
  // firing one list call PER ORG here stampeded 13 simultaneous
  // `GET /core/workspaces` on a 13-org account and saturated the 6-connection
  // HTTP/1.1 pool right as boot data loaded. One call keeps the most likely
  // drill target (the org you're in) instant; other orgs load on open, and the
  // claim fallback covers a click-before-fetch.
  onCreate: (el, s) => {
    const root = s && s.root
    if (!root || typeof setTimeout === 'undefined') return
    let tries = 0
    const poll = () => {
      // A warm boot mounts from the cached snapshot: wait for bootShell's
      // reconcile (it clears _bootFromCache and delivers the boot's list as
      // root.workspaces) before deciding whether a read is needed.
      if (root._bootFromCache) {
        if (++tries < 30) setTimeout(poll, 500)
        return
      }
      if (el.scope.orgs(root).length && el.sdk && el.sdk.execute) {
        const active = el.scope.activeOrg(root)
        // The boot already answered the active org's list: bootShell seeds
        // root.orgWorkspaces[<active org>] (cold path) and root.workspaces
        // (both paths). Re-reading it here was a second
        // GET /core/workspaces?organization= for rows the root already held.
        // The menu-open refresh (openOrgSwitcherMenu) still re-reads on open.
        const seeded = active && root.orgWorkspaces && root.orgWorkspaces[active.id]
        const fromBoot =
          active && Array.isArray(root.workspaces)
            ? root.workspaces.filter(
                (w) => w && String(w.org_id || w.organization || '') === String(active.id)
              )
            : []
        if (!active || !active.id || Array.isArray(seeded)) return
        if (fromBoot.length) {
          root.update({ orgWorkspaces: { ...(root.orgWorkspaces || {}), [active.id]: fromBoot } })
          return
        }
        el.scope.prefetchOrgWorkspaces(root, el.sdk, [active])
      } else if (++tries < 30) {
        setTimeout(poll, 500)
      }
    }
    setTimeout(poll, 800)
  },

  // Trigger styled to match HeaderBreadcrumb's `Button_page` / `Button_project`
  // (company/brand/components/HeaderBreadcrumb.js) so the org switcher reads as
  // a peer of the breadcrumb dropdowns elsewhere in the product chrome.
  //
  // Migrated 2026-06-03 from a bespoke `_wsMenuOpen` toggle + sibling
  // `Menu` panel to the centralized @symbo.ls/dropdown plugin. The
  // panel body lives in `AppOrgSwitcherMenu` (below) which the plugin
  // renders inside its single shell-mounted DropdownRoot. Plugin owns
  // positioning (auto-flip), outside-click dismiss, ESC. `_wsMenuOpen`
  // is kept as a state carrier because AppOrgSwitcherItem.onClick still
  // writes it on activation (closes the dropdown by setting it to
  // false — see the `closeDropdown` call dispatched there).
  Trigger: {
    extends: ['Button', 'AppControl'],
    compact: true,
    theme: 'tertiary',
    gap: 'Z',
    flow: 'row-reverse',
    padding: 'Y2 Z2',
    // Header-island chrome runs on the COMPACT control scale — the brand
    // Button's capsule minHeight (controlH 44px) stretched HeaderRight to
    // 47px against its siblings' 38px island gauge (2026-08-08 per Nika
    // "top right avatar navbar somehow got thicker").
    minHeight: 'controlHCompact',
    // Use a physical touch row so the switcher and invite button keep
    // separate hit areas even in a compact header.
    '@coarse': { ...touchRow, ':after': { display: 'none' } },
    icon: 'arrow angle down',
    color: 'title',
    fontWeight: '400',
    // No explicit font-size (2026-06-08 per Nika "remove AppOrgSwitcher
    // Label fontsize") — the label inherits the surrounding header's font.
    cursor: 'pointer',
    'aria-label': (el) => el.call('polyglot', 'shell.orgSwitcher.switchAria'),
    'aria-haspopup': 'menu',
    // Absolute gauge, not the em-relative `A`. The Trigger deliberately sets
    // no font-size (see the note above), so an em-sized icon inherits whatever
    // the surrounding header happens to be and lands off the row's gauge —
    // measured 13.25px here against HeaderTabs' 14.16px for the identical
    // `boxSize: 'A'`. `iconSm` is a fixed 16px, so every header control agrees.
    Icon: { boxSize: 'iconSm', minWidth: 'iconSm' },
    // Pointer = onMousedown (fires on the original target before a chrome
    // re-render can swap the node — the dead-click window, worst with a
    // PageSlot's load churn); keyboard = detail-0 click. The guard stops one
    // gesture double-firing the open (a same-id re-open remounts the panel).
    onMousedown: (e, el, s) => {
      if (e.button !== 0) return
      el.scope.openOrgSwitcherMenu(e, el, s)
    },
    onClick: (e, el, s) => {
      e.stopPropagation()
      if (e.detail !== 0) return
      el.scope.openOrgSwitcherMenu(e, el, s)
    },
    Label: {
      tag: 'span',
      text: (el, s) =>
        el.scope.activeOrg(s.root)?.name || el.call('polyglot', 'shell.orgSwitcher.orgLabel')
    }
  },

  // Invite icon next to the switcher — opens the shared InviteDropdown
  // (new-member form + magic link + members + roles) anchored to the icon
  // (2026-06-09 per Nika "add invite icon next to orgswitcher which uses
  // Invite dropdown").
  InviteBtn: {
    // AN ICON BUTTON IS A SQUARE (Nika 2026-09-26: "all icon buttons should
    // have 1/1 aspect ratio"; DESIGN-WL-ICON-BUTTONS-SQUARE-1). It extended
    // brand `Button` with `Y2` padding around a 16px glyph under a 36px
    // floor — a 29x36 control (measured, A2-14; crm-w1 read it 30x36 on dev
    // 2c2b6d978). Now the kit's icon-only square, CompactIconButton: the
    // brand icon law restated at the call site until the brand publish
    // lands — controlHCompact on both axes, aspect 1 / 1, padding 0,
    // flex-shrink 0, border-box. The house tooltip, fed by `title`
    // (TitleTooltipTrigger keeps the native one off).
    extends: ['CompactIconButton', 'NavbarTooltipTrigger'],
    icon: 'userPlus',
    theme: 'tertiary',
    color: 'title',
    // The visible control occupies the complete touch target.
    '@coarse': {
      ...touchRow,
      ':after': { display: 'none' }
    },
    'aria-label': (el) => el.call('polyglot', 'shell.orgSwitcher.invite'),
    // The invite panel is a dialog (it holds a form), not a menu.
    'aria-haspopup': 'dialog',
    title: (el) => el.call('polyglot', 'shell.orgSwitcher.invite'),
    // Single brand `userPlus` glyph (user + corner plus) — replaced the
    // composed UserIcon + PlusBadge overlay (2026-06-10 per Nika). Named by
    // `icon` above (IconButton's Icon renders only when the button names
    // one); sized here.
    Icon: { boxSize: 'iconSm', minWidth: 'iconSm' },
    // Same pointer/keyboard split as the Trigger above — see that comment.
    onMousedown: (e, el, s) => {
      if (e.button !== 0) return
      el.scope.openInviteDropdown(e, el, s)
    },
    onClick: (e, el, s) => {
      e.stopPropagation()
      if (e.detail !== 0) return
      el.scope.openInviteDropdown(e, el, s)
    }
  }
}

// Body-only menu rendered inside the dropdown plugin's DropdownRoot
// Panel. Same content as the legacy AppOrgSwitcher.Menu but without
// position/zIndex/outside-click handling (the plugin owns those). The
// `Orgs`, `AdminLink`, `CreateOrg` shapes are unchanged from the legacy
// menu; `DeleteOrg` moved to Settings › Danger zone (Q10 A).
export const AppOrgSwitcherMenu = {
  // FA204 / frank trap — see AppOrgSwitcher.scope's header comment above for
  // the full rationale. `orgs`/`activeOrg` (+ their dependency chain) are
  // duplicated verbatim (object-literal scope properties can't reference
  // each other across different top-level components); `slideOrgSwitcher`
  // is pure DOM manipulation with no shared state, duplicated the same way
  // into AppOrgSwitcherItem's scope below.
  scope: {
    mongoActiveOrgId(root) {
      const org = root && root.org
      return (org && (org.id || org._id)) || ''
    },
    rawOrgs(root) {
      // the switcher's list: functions/visibleOrgs.js (see `_rawOrgs`)
      const fromState = Array.isArray(root?.orgs) ? root.orgs : null
      return visibleOrgs(fromState || [], {
        keep: [root?.org?.id, root?.org?._id, root?.org?.slug, root?._pendingActiveOrgId]
      })
    },
    orgs(root) {
      const raw = this.rawOrgs(root)
      if (!raw.length) return []
      const invalid = root?._invalidOrgs
      if (Array.isArray(invalid) && invalid.length) {
        const skip = new Set(invalid)
        const filtered = raw.filter((o) => !skip.has(o.id))
        return filtered.length ? filtered : raw
      }
      return raw
    },
    workspaces(root) {
      const byOrg = root && root.orgWorkspaces
      if (!byOrg || typeof byOrg !== 'object') return []
      const out = []
      for (const k of Object.keys(byOrg)) {
        const list = byOrg[k]
        if (Array.isArray(list)) out.push(...list)
      }
      return out
    },
    activeWsId(root) {
      if (root?.workspace?.id) return String(root.workspace.id)
      try {
        const ls = getActiveWorkspacePointer()
        if (ls) return ls
      } catch (err) {
        console.warn('[AppOrgSwitcherMenu] read activeWorkspace failed:', err?.message || err)
      }
      return ''
    },
    activeOrg(root) {
      const orgs = this.orgs(root)
      const claimOrgId = this.mongoActiveOrgId(root)
      if (!orgs.length) {
        const stateOrg = root?.org
        if (stateOrg && (stateOrg.id || stateOrg._id)) {
          return { id: stateOrg.id || stateOrg._id, name: stateOrg.name || stateOrg.displayName }
        }
        if (claimOrgId) return { id: claimOrgId, name: root?.org?.name || '' }
        return null
      }
      const pendingId = root?._pendingActiveOrgId
      if (pendingId) {
        const pending = orgs.find((o) => o.id === pendingId)
        if (pending) return pending
      }
      if (claimOrgId) {
        const fromClaim = orgs.find((o) => o.id === claimOrgId)
        if (fromClaim) return fromClaim
      }
      const activeId = this.activeWsId(root)
      const w = this.workspaces(root).find((x) => (x.id || x.workspace_id || x._id) === activeId)
      if (!w) return orgs[0] || null
      return orgs.find((o) => o.id === (w.org_id || w.organization)) || null
    },
    // The org list under the filter: a case- and accent-insensitive match on
    // the name. An empty query is the whole list.
    matchingOrgs(root, query) {
      const orgs = this.orgs(root)
      const fold = (v) =>
        String(v || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
      const q = fold(query).trim()
      if (!q) return orgs
      return orgs.filter((o) => fold(o.name || o.displayName).includes(q))
    },
    // The menu's height is pinned to L1's (see onInit); a filtered list
    // changes L1, so re-pin it after the list has rendered.
    remeasureL1(el) {
      const run = () => this.slideOrgSwitcher(el && el.node, 0)
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run)
      else run()
    },
    // Imperative because the dropdown panel doesn't re-render content on
    // state change and v3.14 freezes reactive transform/height. `fromNode`
    // is any node inside the menu; level 1 = workspaces slid in, 0 = back
    // to orgs.
    slideOrgSwitcher(fromNode, level, orgId, orgName) {
      if (!fromNode || typeof fromNode.closest !== 'function') return
      const menu = fromNode.closest('[role="menu"]')
      if (!menu) return
      const slider = menu.querySelector('[data-org-slider]')
      // The level on the menu: the menu's static rules take the layer that
      // is off stage out of paint and out of the Tab order after the slide.
      menu.setAttribute('data-org-level', level === 1 ? '1' : '0')
      if (level === 1) {
        menu.querySelectorAll('[data-ws-group]').forEach((g) => {
          g.style.display = g.getAttribute('data-ws-group') === orgId ? '' : 'none'
        })
        const title = menu.querySelector('[data-org-l2-title]')
        if (title && orgName) title.textContent = orgName
        if (slider) slider.style.transform = 'translateX(-100%)'
        const l2 = menu.querySelector('[data-org-l2]')
        if (l2) menu.style.height = l2.scrollHeight + 'px'
      } else {
        if (slider) slider.style.transform = 'translateX(0)'
        const l1 = menu.querySelector('[data-org-l1]')
        if (l1) menu.style.height = l1.scrollHeight + 'px'
      }
    }
  },
  // House MenuPanel chrome — the body RELAYS on the one house dropdown
  // surface and paints none of its own (Nika 2026-09-02, pinned by
  // dropdownPanelSurfaceRelay.test.js); the slide mechanics need their own box
  // model — no padding (the L1/L2 layers pad themselves), hidden overflow,
  // free height.
  extends: 'MenuPanel',
  tag: 'div',
  minWidth: 'min(G, calc(100dvw - var(--spacing-B)))',
  width: 'max-content',
  maxWidth: 'calc(100dvw - var(--spacing-B))',
  // The menu's OWN state: the org filter (it used to land on the dropdown
  // layer's state, which every other dropdown body shares). Declared, so the
  // updates are never dropped.
  state: { orgQuery: '' },
  textAlign: 'left',
  padding: '0',
  maxHeight: 'none',
  overflow: 'hidden',
  position: 'relative',
  // 2-level slide: orgs → that org's workspaces, committing on a workspace
  // pick (2026-06-09 per Nika). The dropdown panel doesn't re-render its
  // content on state change and v3.14 freezes reactive transform/height, so
  // the slide + height are driven imperatively on the nodes via data-* hooks
  // (see _slideOrgSwitcher) by AppOrgSwitcherItem (drill) and the L2 back row.
  // Motion on the overlay spring (DESIGN_SPEC §9: `C springBezier`; it was a
  // raw 280ms curve of its own).
  transition: 'height C springBezier',
  // ONE LAYER ON STAGE (the overflow law, HOME_AMBIENT_SKIN_SPEC §3b rule 1:
  // the hidden overflow above clips the SLIDE, never a control). The layer
  // the slide moved off stage was still painted beyond the clip and still in
  // the Tab order — Tab from the last org reached an invisible Back button.
  // It leaves with the slide: visible at once when it comes in, hidden only
  // after the `C` spring when it goes out, so the motion still shows both.
  // `data-org-level` is written by slideOrgSwitcher; no attribute = level 0.
  '& [data-org-l1], & [data-org-l2]': { transition: 'visibility 0s' },
  '&:not([data-org-level="1"]) [data-org-l2]': {
    visibility: 'hidden',
    transition: 'visibility 0s linear C'
  },
  '&[data-org-level="1"] [data-org-l1]': {
    visibility: 'hidden',
    transition: 'visibility 0s linear C'
  },
  onInit: (el) => {
    if (typeof requestAnimationFrame === 'undefined' || !el.node) return
    // Always (re)open sized to L1. Clear any stale inline height FIRST — a height
    // left over from a previous open's L2 drill (or a single-rAF measurement
    // taken before the orgs list had laid out) would otherwise persist and
    // mis-size the panel, reading as a big gap above/below the content. Then
    // measure after a DOUBLE rAF so the L1 list is actually flushed before we
    // pin the height. (2026-06-22 per Nika "OrgSwitcher content sometimes has
    // big padding top".)
    el.node.style.height = ''
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const l1 = el.node && el.node.querySelector('[data-org-l1]')
        if (l1 && el.node) el.node.style.height = l1.scrollHeight + 'px'
      })
    )
  },

  Slider: {
    attr: { 'data-org-slider': 'true' },
    position: 'relative',
    flow: 'x',
    width: '100%',
    align: 'start',
    transition: 'transform C springBezier',

    // THE LIST SCROLLS, THE FOOTER DOES NOT.
    //
    // The smbls dropdown portal that hosts this menu caps itself at 70vh and
    // scrolls (inline `max-height: 70vh; overflow: auto` on the portal node —
    // MenuPanel's own 60vh is overridden to `none` above, so the portal's cap
    // is the one that binds). The footer rows — Admin, Create organization
    // (Delete organization then too; it moved to Settings › Danger zone,
    // Q10 A) — sit AFTER the org list, so on an account with many
    // orgs they were pushed past that cap and out of sight. Measured on
    // my.symbols.app 2026-09-04 with 18 orgs: L1 was 706px tall in a 600px
    // port, and the Admin row's top sat at y=646, 46px below the fold. The
    // portal does scroll, but macOS overlay scrollbars paint nothing at rest,
    // so the menu simply looked like it had no admin link at all.
    //
    // So L1 takes the cap itself and gives it to the ORG LIST: the list is the
    // part that grows without bound, the footer is fixed-size and must always
    // be reachable. `minHeight: 0` is required — a flex item's default
    // `min-height: auto` refuses to shrink below its content and would keep
    // the overflow on the outside. The 70dvh literal mirrors the portal's own
    // 70vh cap — dynamic units, so on iOS Safari (where vh is the LARGE
    // viewport, the URL bar hidden) the list fits the screen the person
    // actually sees and never exceeds the portal (dvh <= vh everywhere); L2
    // below carries the same pair for the same reason.
    L1: {
      attr: { 'data-org-l1': 'true' },
      width: '100%',
      flexShrink: '0',
      flow: 'y',
      padding: 'Z',
      maxHeight: '70dvh',
      boxSizing: 'border-box',

      // A long membership list gets a filter (DESIGN_SPEC §9: more than eight
      // orgs). The kit's compact search capsule; the query lives on the
      // menu's own state; the menu re-measures L1 after the list changes.
      OrgSearch: {
        extends: 'AppFieldSearch',
        hide: (el, s) => el.scope.orgs(s.root).length <= 8,
        flexShrink: '0',
        marginBottom: 'Y',
        placeholder: (el) => el.call('polyglot', 'common.action.search'),
        aria: { label: (el) => el.call('polyglot', 'shell.orgSwitcher.switchAria') },
        value: (el, s) => s.orgQuery || '',
        onInput: (e, el, s) => {
          s.update({ orgQuery: (e.target && e.target.value) || '' })
          el.scope.remeasureL1(el)
        },
        onClear: (e, el, s) => {
          s.update({ orgQuery: '' })
          el.scope.remeasureL1(el)
        }
      },

      Orgs: {
        flow: 'y',
        // The only flex item that may give up height. Everything below it in
        // L1 is pinned with `flexShrink: '0'` — without that the 1px Divider
        // (content height 0) is the cheapest thing for the browser to shrink,
        // and it disappears first.
        flex: '1 1 auto',
        minHeight: '0',
        overflowY: 'auto',
        // Don't chain the list's edge-bounce to the page behind the menu —
        // the same guideline MenuPanel applies to its own scroller.
        overscrollBehavior: 'contain',
        childExtends: 'AppOrgSwitcherItem',
        childrenAs: 'state',
        children: (el, s) => el.scope.matchingOrgs(s.root, s.orgQuery)
      },

      NoOrgMatches: {
        tag: 'p',
        flexShrink: '0',
        margin: '0',
        padding: 'Y Z1',
        fontSize: 'Z1',
        color: 'caption',
        hide: (el, s) =>
          !(s.orgQuery || '').trim() || el.scope.matchingOrgs(s.root, s.orgQuery).length > 0,
        text: '{{ common.state.noMatches | polyglot }}'
      },

      // The footer is set apart by space, not a hairline (no rules inside a
      // rounded surface) — a pinned 1px spacer inside its margin.
      Divider: { height: '1px', flexShrink: '0', margin: 'Y 0' },

      // Admin link — only visible when the user holds an org-management role
      // on the currently active org. Tier hierarchy mirrors the server-side
      // ORG_MGMT_ROLES set used by tickets/functions/cycleStore.js's
      // `canManageCycles`: owner / co-owner / admin / maintainer. Routes to
      // /admin (workspace/pages/admin/admin.js) — the Users & Roles panel.
      AdminLink: {
        // House context-dropdown row chrome (padding/round/fontSize/hover).
        extends: 'MenuPanelItem',
        flexShrink: '0',
        hide: (el, s) => {
          const active = el.scope.activeOrg(s.root)
          const role = active?.role
          return !(
            role === 'owner' ||
            role === 'co-owner' ||
            role === 'admin' ||
            role === 'maintainer'
          )
        },
        text: '{{ shell.orgSwitcher.admin | polyglot }}',
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('closeDropdown', 'appOrgSwitcher')
          el.router('/admin', el.getRoot())
        }
      },

      // "Delete organization" is NOT here (Q10 A, Nika 2026-09-26): it moved to
      // Settings › Danger zone (MemberProfile.ProfileDanger.DeleteOrgRow), with
      // its confirm step. A destructive org action does not belong in the menu
      // people open to switch; the switcher keeps Admin and + Create.

      // Footer action — opens the inline AppCreateOrgModal (sibling
      // component, root-mounted) instead of routing to a canvas URL. The
      // old `canvasUrl('/organizations/new')` resolved to
      // `https://canvas.<tier>.symbols.app/organizations/new`, but that
      // host is NXDOMAIN and the path isn't routed on canvas either —
      // bug #2186. Keeping the create flow in-shell also avoids losing
      // the user's current workspace context.
      CreateOrg: {
        // House context-dropdown row chrome; footer action stays muted
        // (caption) and brightens to title on the house theme-aware hover.
        extends: 'MenuPanelItem',
        flexShrink: '0',
        color: 'caption',
        // House MenuPanelItem hover (the `hover` wash token) plus a
        // caption→title brighten for the muted footer action.
        ':hover': {
          color: 'title',
          background: 'hover'
        },
        text: '{{ shell.orgSwitcher.createOrg | polyglot }}',
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('closeDropdown', 'appOrgSwitcher')
          el.call('openCreateOrgModal')
        }
      }
    },

    // Same cap as L1 (see its comment): the workspace groups are the growing
    // part, the Back header must stay reachable at the top of the port.
    L2: {
      attr: { 'data-org-l2': 'true' },
      // Only L1 determines the intrinsic menu width; L2 slides into its box.
      position: 'absolute',
      left: '100%',
      top: '0',
      width: '100%',
      flexShrink: '0',
      flow: 'y',
      padding: 'Z',
      maxHeight: '70dvh',
      boxSizing: 'border-box',

      Back: {
        attr: { 'data-org-back': 'true' },
        tag: 'button',
        type: 'button',
        flexShrink: '0',
        flow: 'x',
        align: 'center left',
        gap: 'Z',
        width: '100%',
        borderWidth: '0',
        background: 'transparent',
        cursor: 'pointer',
        // House context-dropdown row chrome (padding/round/fontSize/hover);
        // keeps the bold weight + back-icon layout of the L2 header.
        padding: 'Y Z1',
        round: 'radiusControl',
        color: 'title',
        fontWeight: '700',
        fontSize: 'Z1',
        marginBottom: 'V',
        // House MenuPanelItem hover (the `hover` wash token).
        ':hover': { background: 'hover' },
        BackIcon: {
          extends: 'Icon',
          name: 'arrowAngleLeft',
          boxSize: 'A',
          flexShrink: '0'
        },
        Title: {
          tag: 'span',
          attr: { 'data-org-l2-title': 'true' },
          flex: '1',
          minWidth: '0',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          text: '{{ shell.orgSwitcher.workspaces | polyglot }}'
        },
        onClick: (e, el) => {
          e.stopPropagation()
          el.scope.slideOrgSwitcher(el.node, 0)
        }
      },

      Groups: {
        flow: 'y',
        flex: '1 1 auto',
        minHeight: '0',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        childExtends: 'AppWsGroup',
        childrenAs: 'state',
        children: (el, s) => el.scope.orgs(s.root)
      }
    }
  }
}

// One org row. Activating an org means switching to its home workspace,
// which triggers RLS to re-scope every tenant-table query.
export const AppOrgSwitcherItem = {
  // FA204 / frank trap — see AppOrgSwitcher.scope's header comment above for
  // the full rationale. `activeOrg` (+ chain) and `slideOrgSwitcher` are
  // duplicated verbatim from AppOrgSwitcherMenu's own scope.
  scope: {
    mongoActiveOrgId(root) {
      const org = root && root.org
      return (org && (org.id || org._id)) || ''
    },
    rawOrgs(root) {
      // the switcher's list: functions/visibleOrgs.js (see `_rawOrgs`)
      const fromState = Array.isArray(root?.orgs) ? root.orgs : null
      return visibleOrgs(fromState || [], {
        keep: [root?.org?.id, root?.org?._id, root?.org?.slug, root?._pendingActiveOrgId]
      })
    },
    // The row MARK's image (Nika 2026-10-31): the org's own logoSvg wins,
    // else the company profile logo /auth/me sends as `profileLogoSrc` (https
    // only), else nothing (the letter). Reads only the ROW's OWN keys: a
    // `childrenAs: 'state'` row reads a key it lacks from the ROOT state, and
    // a root-level logo must never paint every row. A src that failed to load
    // (onError records it) is the letter too.
    orgMarkSrc(s) {
      if (!s) return ''
      // read first (a tracked read: a later write re-runs the mark), then
      // keep the value only when the row itself holds the key
      const own = (k) => {
        const v = s[k]
        return Object.prototype.hasOwnProperty.call(s, k) ? v : undefined
      }
      const branding = own('branding')
      const svg = own('logoSvg') || (branding && branding.logoSvg)
      let src = ''
      if (typeof svg === 'string' && /^\s*<svg[\s>]/i.test(svg)) {
        src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg.trim())
      } else {
        const url = own('profileLogoSrc')
        if (typeof url === 'string' && /^https:\/\/\S+$/i.test(url)) src = url
      }
      return src && own('orgMarkFailedSrc') !== src ? src : ''
    },
    orgs(root) {
      const raw = this.rawOrgs(root)
      if (!raw.length) return []
      const invalid = root?._invalidOrgs
      if (Array.isArray(invalid) && invalid.length) {
        const skip = new Set(invalid)
        const filtered = raw.filter((o) => !skip.has(o.id))
        return filtered.length ? filtered : raw
      }
      return raw
    },
    workspaces(root) {
      const byOrg = root && root.orgWorkspaces
      if (!byOrg || typeof byOrg !== 'object') return []
      const out = []
      for (const k of Object.keys(byOrg)) {
        const list = byOrg[k]
        if (Array.isArray(list)) out.push(...list)
      }
      return out
    },
    activeWsId(root) {
      if (root?.workspace?.id) return String(root.workspace.id)
      try {
        const ls = getActiveWorkspacePointer()
        if (ls) return ls
      } catch (err) {
        console.warn('[AppOrgSwitcherItem] read activeWorkspace failed:', err?.message || err)
      }
      return ''
    },
    activeOrg(root) {
      const orgs = this.orgs(root)
      const claimOrgId = this.mongoActiveOrgId(root)
      if (!orgs.length) {
        const stateOrg = root?.org
        if (stateOrg && (stateOrg.id || stateOrg._id)) {
          return { id: stateOrg.id || stateOrg._id, name: stateOrg.name || stateOrg.displayName }
        }
        if (claimOrgId) return { id: claimOrgId, name: root?.org?.name || '' }
        return null
      }
      const pendingId = root?._pendingActiveOrgId
      if (pendingId) {
        const pending = orgs.find((o) => o.id === pendingId)
        if (pending) return pending
      }
      if (claimOrgId) {
        const fromClaim = orgs.find((o) => o.id === claimOrgId)
        if (fromClaim) return fromClaim
      }
      const activeId = this.activeWsId(root)
      const w = this.workspaces(root).find((x) => (x.id || x.workspace_id || x._id) === activeId)
      if (!w) return orgs[0] || null
      return orgs.find((o) => o.id === (w.org_id || w.organization)) || null
    },
    slideOrgSwitcher(fromNode, level, orgId, orgName) {
      if (!fromNode || typeof fromNode.closest !== 'function') return
      const menu = fromNode.closest('[role="menu"]')
      if (!menu) return
      const slider = menu.querySelector('[data-org-slider]')
      // The level on the menu: the menu's static rules take the layer that
      // is off stage out of paint and out of the Tab order after the slide.
      menu.setAttribute('data-org-level', level === 1 ? '1' : '0')
      if (level === 1) {
        menu.querySelectorAll('[data-ws-group]').forEach((g) => {
          g.style.display = g.getAttribute('data-ws-group') === orgId ? '' : 'none'
        })
        const title = menu.querySelector('[data-org-l2-title]')
        if (title && orgName) title.textContent = orgName
        if (slider) slider.style.transform = 'translateX(-100%)'
        const l2 = menu.querySelector('[data-org-l2]')
        if (l2) menu.style.height = l2.scrollHeight + 'px'
      } else {
        if (slider) slider.style.transform = 'translateX(0)'
        const l1 = menu.querySelector('[data-org-l1]')
        if (l1) menu.style.height = l1.scrollHeight + 'px'
      }
    }
  },
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center left',
  gap: 'Z1',
  width: '100%',
  // Row chrome = the house MenuPanelItem recipe (kept inline — the active
  // background factory needs to interleave with it): Y/Z1 padding, the
  // control radius, Z1 fontSize, theme-aware :hover wash.
  padding: 'Y Z1',
  round: 'radiusControl',
  borderWidth: '0',
  // Active row gets the subtle FilesSidebar active wash (white @10%, same as
  // /files FilesScopeSeg / FilesTypeFilterRow) instead of the harsh near-opaque
  // overlayStrong block (2026-06-14 per Nika "show active item by background
  // just like FilesSidebar"); the active row keeps that wash on hover.
  background: (el, s) => {
    const active = el.scope.activeOrg(s.root)
    // Non-active rows return undefined (NOT 'transparent'): a factory background
    // renders as an INLINE style, and an inline `background:var(--color-transparent)`
    // outranks the `:hover` CLASS rule below — that's why the rows never washed on
    // hover. With no inline bg the `:hover` rule applies; the active row keeps its
    // inline wash (which correctly persists on hover, as intended).
    return active && active.id === s.id ? 'selected' : undefined
  },
  cursor: 'pointer',
  fontSize: 'Z1',
  fontWeight: '500',
  color: 'title',
  textAlign: 'left',
  // Expose the active org to assistive tech, not just the background wash.
  'aria-current': (el, s) => {
    const active = el.scope.activeOrg(s.root)
    return active && active.id === s.id ? 'true' : 'false'
  },
  // House MenuPanelItem :hover wash — the `hover` token as a FLAT prop (a
  // `:hover` block with only `@dark`/`@light` sub-blocks emits NOTHING through
  // css-in-props; the adaptive token needs no split anyway). The active wash
  // lives on the base `background` above.
  ':hover': { background: 'hover' },
  ':active': { background: 'selected' },

  // The org's MARK — a small squircle with its first letter on the neutral
  // plate (DESIGN_SPEC §9: "rows = org squircle + name + role micro-label").
  // One grapheme, never re-cased: a Georgian name keeps its Mkhedruli letter.
  OrgMark: {
    tag: 'span',
    display: 'flex',
    align: 'center center',
    flexShrink: '0',
    boxSize: 'avatarXs',
    round: 'radiusControl',
    background: 'widgetPlate',
    color: 'title',
    fontSize: 'Z',
    fontWeight: '500',
    lineHeight: '1',
    overflow: 'hidden',
    ariaHidden: 'true',
    // the letter only while no logo shows (scope.orgMarkSrc)
    text: (el, s) =>
      el.scope?.orgMarkSrc?.(s)
        ? ''
        : Array.from(String(s.name || s.displayName || '').trim())[0] || '',

    // The logo, when the org has one: the org's own logoSvg, else the company
    // profile logo. Decorative (the row's Name carries the label). A logo that
    // fails to load records its src on the row, so the mark shows the letter.
    Img: {
      boxSize: '100%',
      objectFit: 'cover',
      round: 'radiusControl',
      alt: '',
      hide: (el, s) => !el.scope?.orgMarkSrc?.(s),
      src: (el, s) => el.scope?.orgMarkSrc?.(s) || null,
      onError: (e, el, s) => {
        const src = el.scope?.orgMarkSrc?.(s)
        if (src && s && typeof s.update === 'function') s.update({ orgMarkFailedSrc: src })
      }
    }
  },

  Name: {
    tag: 'span',
    flex: '1 1 auto',
    minWidth: '0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    ':hover': { textOverflow: 'clip' },
    '&:hover > span': { width: 'max-content', overflow: 'visible' },
    onMouseenter: (e, el) => {
      const node = el.node
      const text = el.Text?.node
      if (!node || !text || typeof text.animate !== 'function') return
      text.getAnimations().forEach((animation) => animation.cancel())
      const distance = text.scrollWidth - node.clientWidth
      if (distance <= 0) return
      if (
        typeof matchMedia === 'function' &&
        matchMedia('(prefers-reduced-motion: reduce)').matches
      )
        return
      // Pause at each end, then travel back; leaving restores the ellipsis.
      const travel = Math.max(1200, distance * 35)
      const pause = 700
      const duration = travel + pause * 2
      text.animate(
        [
          { transform: 'translateX(0)', offset: 0 },
          { transform: 'translateX(0)', offset: pause / duration },
          { transform: `translateX(-${distance}px)`, offset: 1 - pause / duration },
          { transform: `translateX(-${distance}px)`, offset: 1 }
        ],
        { duration, iterations: Infinity, direction: 'alternate', easing: 'linear' }
      )
    },
    onMouseleave: (e, el) => {
      el.Text?.node?.getAnimations().forEach((animation) => animation.cancel())
    },
    onDestroy: (el) => {
      el.Text?.node?.getAnimations().forEach((animation) => animation.cancel())
    },
    Text: {
      tag: 'span',
      display: 'block',
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      text: (el, s) => s.name
    }
  },
  // Per-org attention badge. Reads from `root.orgNotifCount[orgId]` —
  // a simple count keyed by org id. Populated by whatever cross-org
  // notifications aggregator lands server-side (see NEEDED_FOR_SERVER
  // §I8); renders an empty string when the count is 0/undefined so the
  // slot stays invisible rather than showing stale layout.
  NotifBadge: {
    tag: 'span',
    fontSize: 'Y2',
    fontWeight: '700',
    // Reviewed (fixed-colour sweep): kept — fixed alert-badge pairing
    // (background is the non-adaptive `red` accent), the same convention
    // Messenger/NotificationBell/meet's badges all use.
    color: 'white',
    // Fixed alert-badge pairing, deliberately NOT theme-adaptive — an alert
    // reads red on both shells. But brand `red` (#EB6650) is tuned as a PLATE
    // colour, and white on it measures 3.21:1: fine for a dot (3:1 non-text
    // floor) and a fail for a COUNT, which is text. `redDeep` is the same
    // alert red deepened — white on #A83520 = 6.53:1. Measured across every
    // route by the aa-contrast sweep, 2026-08-09; 15 violation groups traced
    // to this one badge shape.
    background: 'redDeep',
    round: 'radiusPill',
    padding: 'V Z',
    minWidth: 'Z1',
    textAlign: 'center',
    text: (el, s) => {
      const count = s.root?.orgNotifCount?.[s.id] || 0
      return count > 0 ? (count > 99 ? '99+' : String(count)) : ''
    },
    // Collapse the badge entirely when count is 0. Using visibility
    // keeps the flex gap consistent; display:none would cause layout
    // jitter on state updates in DOMQL v3.14.
    display: (el, s) => {
      const count = s.root?.orgNotifCount?.[s.id] || 0
      return count > 0 ? 'inline-block' : 'none'
    }
  },
  // The role as the mono micro-label (it labels data); `caption` rather than
  // the label's own `inactive` — the active row sits on the `selected` wash,
  // where `inactive` drops under 4.5:1 (AppMicroLabel's note).
  Role: {
    extends: 'AppMicroLabel',
    flexShrink: '0',
    whiteSpace: 'nowrap',
    color: 'caption',
    text: (el, s) => s.role || 'member'
  },
  // The current org: the check, in ink — selection is monochrome (P5; it was
  // green). An ICON, not the ✓ text glyph.
  Check: {
    extends: 'Icon',
    flexShrink: '0',
    name: 'check',
    boxSize: 'A',
    color: 'title',
    hide: (el, s) => {
      const active = el.scope.activeOrg(s.root)
      return !(active && active.id === s.id)
    }
  },

  // Chevron hint that the row drills into workspaces — quiet ink, no opacity.
  Chevron: {
    extends: 'Icon',
    flexShrink: '0',
    name: 'arrowAngleRight',
    boxSize: 'Z1',
    color: 'inactive'
  },

  // Drilling into an org slides the menu to its workspaces (level 2). The
  // actual switch commits when a workspace is picked (AppWsItem) — 2026-06-09
  // per Nika, the 2-level org → workspace dropdown.
  onClick: (e, el, s) => {
    e.stopPropagation()
    el.scope.slideOrgSwitcher(el.node, 1, s.id, s.name)
  }
}

// Commit a switch to `org` with a specific home workspace (the picked one).
// The org-row switch logic, extracted — now the level-2 workspace rows call it.
// THE SWITCH DEADLINE (work.astio.ai 2026-10-08, Nika: an org picked in the
// header menu left "Switching workspace" on screen for ever). The switch
// awaited `sdk.switchOrg` with no bound; every step inside it is an unbounded
// network wait (the PATCH, the token refresh behind it, the workspace hop), so
// one that never answered never reached `_endSwitching`. Now the await races
// ORG_SWITCH_DEADLINE_MS; `_wsSwitching` ALWAYS clears (finally) and the toast
// offers a retry (`retryOrgSwitch` -> globalThis.__orgSwitchRetry).
// Follow-ups M1–M3 (review-two-hotfixes.md, 2026-10-09):
//  · M3 — the only caller passes a homeWorkspaceId, so the 12 s reload
//    fallback is armed on every switch: the deadline (10 s) fires FIRST and its
//    catch disarms the fallback.
//  · M2 / B1 / B2 — the server is the truth. A timeout always ends in ONE
//    full reload (the boot reads the server's org/workspace): the client never
//    rolls back alone, and no late SDK answer or socket echo can land in a page
//    that was told the switch failed.
//  · M1 — one switch generation (globalThis.__orgSwitchState). The SDK call is
//    shared by key: "Try again" while it is still in flight RE-JOINS it, never
//    races a second one; only the newest generation clears the loader flags.
const ORG_SWITCH_DEADLINE_MS = 10000
const ORG_SWITCH_TIMEOUT = 'ORG_SWITCH_TIMEOUT'
const _withSwitchDeadline = (work) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      const err = new Error('the switch did not answer in ' + ORG_SWITCH_DEADLINE_MS / 1000 + ' s')
      err.code = ORG_SWITCH_TIMEOUT
      reject(err)
    }, ORG_SWITCH_DEADLINE_MS)
    let pending
    try {
      pending = Promise.resolve(work())
    } catch (err) {
      pending = Promise.reject(err)
    }
    pending.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (err) => {
        clearTimeout(timer)
        reject(err)
      }
    )
  })

const _orgSwitchState = () => {
  if (!globalThis.__orgSwitchState) globalThis.__orgSwitchState = { gen: 0, pending: null }
  return globalThis.__orgSwitchState
}
// The in-flight SDK switch for `key`, shared: a second commit for the same
// target re-joins it instead of starting another.
const _sharedSwitchCall = (key, start) => {
  const st = _orgSwitchState()
  if (st.pending && st.pending.key === key) return st.pending.promise
  let run
  try {
    run = Promise.resolve(start())
  } catch (err) {
    run = Promise.reject(err)
  }
  st.pending = { key, promise: run }
  const clear = () => {
    if (st.pending && st.pending.promise === run) st.pending = null
  }
  run.then(clear, clear)
  return run
}

// N4 (Nika 2026-10-09, "Go to that org's own host"): on a white-label host a
// brand only ever shows its own org. A switch to ANOTHER org leaves the host
// with one top-level navigation to that org's own host — its VERIFIED
// white-label domain from the server record (the org's custom-domain rows,
// the same SDK read the admin Domains page uses), else the channel's shell
// host (shared workspaceOrigin). No in-place switch happens there, so the
// half-switch risk (B1/B2) cannot. NO token travels: every remote handoff is
// refused (P0, d16ce904f) — the user signs in on the target host if needed.
// The domain read is bounded; a refusal (only org managers may list the
// rows), an error or a timeout means "no verified domain known".
const ORG_HOST_LOOKUP_MS = 3000
const _orgOwnHostUrl = async (el, orgId, orgSlug) => {
  let domain = null
  try {
    if (el.sdk && typeof el.sdk.listOrgCustomDomains === 'function') {
      const res = await Promise.race([
        Promise.resolve(el.sdk.listOrgCustomDomains(orgId)),
        new Promise((resolve) => setTimeout(() => resolve(null), ORG_HOST_LOOKUP_MS))
      ])
      const rows = (res && (res.customDomains || (res.data && res.data.customDomains))) || []
      const row = Array.isArray(rows) ? rows.find((r) => r && r.domain && r.verifiedAt) : null
      domain = row ? String(row.domain).trim().toLowerCase() : null
    }
  } catch (_) {
    domain = null
  }
  const here = typeof window !== 'undefined' && window.location ? window.location.hostname : ''
  if (domain && /^[a-z0-9.-]+$/.test(domain) && domain !== here) return `https://${domain}/`
  // The shell host of THIS deployment's plane (ruling b): the served channel
  // first — a customer domain's hostname names no channel, and the
  // hostname-only resolver would fall back to the PROD shell on a staging
  // white-label host — then the hostname resolver.
  const channel = deploymentChannel()
  const shell = (channel && channelShellUrl(channel)) || workspaceOrigin()
  try {
    const u = new URL(shell)
    // N5: land in the org the user PICKED (`/w/<slug>/`), not the server's
    // active one; a slug that is not slug-shaped never reaches the path.
    const slug =
      typeof orgSlug === 'string' && /^[a-z0-9][a-z0-9_-]*$/i.test(orgSlug) ? orgSlug : null
    const path = slug ? withWorkspacePrefix(slug, '/').replace(/\/?$/, '/') : '/'
    if (u.hostname !== here) return u.origin + path
  } catch (_) {
    /* no shell origin — stay */
  }
  return null
}

async function _commitOrgSwitch(el, root, org, homeWorkspaceId, label) {
  if (!org) return
  // N4: leaving a white-label host for another org is a navigation, never an
  // in-place switch.
  if (
    String(org.id) !== String(_mongoActiveOrgId(root)) &&
    typeof window !== 'undefined' &&
    window.location &&
    el.call('isCustomDomainHost') === true
  ) {
    el.call('closeDropdown', 'appOrgSwitcher')
    // N7: the host lookup can take up to ORG_HOST_LOOKUP_MS — show the loader
    _startSwitching(root, label || org.name, org.id)
    let target = null
    try {
      target = await _orgOwnHostUrl(el, org.id, org.slug || org.username)
    } finally {
      if (!target) _endSwitching(root)
    }
    if (target) {
      window.location.assign(target)
      return
    }
  }
  const _st = _orgSwitchState()
  const _gen = ++_st.gen
  let _ended = false
  const _end = (extra) => {
    if (_ended) return
    _ended = true
    // a newer switch owns the loader flags now — never clear them under it
    if (_gen !== _st.gen) return
    _endSwitching(root, extra)
  }
  // A workspace pick is EITHER a cross-org switch OR a workspace switch within
  // the org you're already on. sdk.switchOrg early-returns { changed: false }
  // when the org is unchanged and so NEVER federates the workspace — that's the
  // "picked another workspace in my current org and nothing happened" bug. Route
  // same-org picks through switchWorkspace directly. Capture the active org
  // BEFORE _startSwitching sets the optimistic pending id.
  // Mongo only — root.org.id (the resolved active-org row) is the canonical
  // current-org id.
  const _currentOrgId = _mongoActiveOrgId(root)
  const _orgChanged = String(org.id) !== String(_currentOrgId)
  _startSwitching(root, label || org.name, org.id)
  el.call('closeDropdown', 'appOrgSwitcher')
  // Arm the reload fallback BEFORE the switch. The in-place refetch
  // (onWorkspaceChange — fired by sdk.orgSwitched / the claims refresh) calls
  // clearWorkspaceSwitchFallback() on its FIRST line, so arming first lets the
  // common in-place path CANCEL this timer → NO reload, a smooth in-place switch.
  // It used to be armed AFTER `await switchOrg`, by which point onWorkspaceChange
  // had already run and cleared a not-yet-armed timer — so the late timer always
  // survived and hard-reloaded the window ~5s after every switch (the "switches
  // then reloads" bug). The reload is now a true last resort: it only fires if
  // the refetch never runs within 12s (a genuinely stuck federated round-trip).
  if (homeWorkspaceId) {
    clearWorkspaceSwitchFallback()
    if (!globalThis.__wsSwitchFallbackState) globalThis.__wsSwitchFallbackState = { timer: null }
    globalThis.__wsSwitchFallbackState.timer = setTimeout(() => {
      // The bag is created, never deleted, by production code — but this
      // callback runs 12 s later and a throw inside a timer has no catcher,
      // so it never assumes the bag is still there.
      if (globalThis.__wsSwitchFallbackState) globalThis.__wsSwitchFallbackState.timer = null
      if (typeof window !== 'undefined') {
        console.warn('[AppOrgSwitcher] in-place workspace refresh never ran — reloading')
        window.location.reload()
      }
    }, 12000)
  }
  // No local retry here — the SDK itself retries a pre-send
  // NETWORK_UNREACHABLE failure (e.g. a nodemon restart window) at its
  // request chokepoint for both `setActiveOrganization` and
  // `setActiveWorkspace` (see sdk/src/services/BaseService.js
  // RETRY_SAFE_METHOD_NAMES), which `sdk.switchOrg`/`sdk.switchWorkspace`
  // call internally. A second retry loop wrapped around them here just
  // multiplied the wait (4 tries x 1200ms on top of the SDK's own
  // exponential backoff) before a real, non-transient failure could
  // surface to the user.
  try {
    if (!_orgChanged && homeWorkspaceId) {
      // Same org → switch ONLY the workspace (switchOrg would no-op and never
      // federate). switchWorkspace's federation returns { ok: false } instead of
      // throwing on a forbidden/failed bridge call, so surface that as an error
      // rather than silently leaving the user where they were.
      const res = await _withSwitchDeadline(() =>
        _sharedSwitchCall('ws:' + homeWorkspaceId, () => el.sdk.switchWorkspace(homeWorkspaceId))
      )
      if (res && res.ok === false) {
        throw new Error((res.error && res.error.message) || res.error || 'workspace switch failed')
      }
    } else {
      // Cross-org: switchOrg persists the org (Mongo) AND federates the
      // workspace via an inner switchWorkspace, so it emits BOTH
      // sdk.orgSwitched and sdk.workspaceSwitched. Flag the window so the
      // shell's workspaceSwitched handler defers to its orgSwitched handler
      // and the in-place refetch runs once, not twice.
      // The flag lives as long as the SDK call itself (not the deadline): a
      // late answer still emits workspaceSwitched while the call runs.
      await _withSwitchDeadline(() =>
        _sharedSwitchCall('org:' + org.id + ':' + (homeWorkspaceId || ''), () => {
          if (el.sdk) el.sdk.__switchingOrg = true
          const call = Promise.resolve(el.sdk.switchOrg(org.id, { homeWorkspaceId }))
          const off = () => {
            if (el.sdk) el.sdk.__switchingOrg = false
          }
          call.then(off, off)
          return call
        })
      )
    }
    _end()
    if (typeof window !== 'undefined' && org.slug) {
      const m = window.location.pathname.match(/^\/studio\/[^/]+(\/.*)?$/)
      if (m) el.router(`/studio/${org.slug}${m[1] || ''}`, el.getRoot())
      // Phase 4 (I6): generalize the /studio slug-rewrite to the /w/<org-slug>
      // intranet prefix — on a cross-org switch, rewrite THIS tab's URL to the
      // new org so it stays pinned to what it just switched to. The router
      // wrapper trusts an explicitly-prefixed path's slug (planMwtNav), so this
      // survives root.org still being mid-update.
      // ORG-URL-1: guarded off whenever `homeWorkspaceId` is known (see
      // _allowOptimisticUrlRewrite above) — this component's only caller
      // (AppWsItem.onClick) always knows it, and the shell's atomic
      // sdk.orgSwitched listener (app.js _handleOrgSwitched ->
      // syncWorkspaceUrl(..., {rewriteOrg:true})) already rewrites the URL
      // correctly from settled post-switch state. Firing BOTH raced each
      // other and this optimistic version built a malformed URL (e.g.
      // `/w/system/ws_OXN_VBk3Vx` — org updated, workspace id still stale).
      else if (_allowOptimisticUrlRewrite(window.location.pathname, homeWorkspaceId)) {
        const rest = stripWorkspacePrefix(window.location.pathname)
        el.router(withWorkspacePrefix(org.slug, rest), el.getRoot())
      }
    }
  } catch (err) {
    // Switch failed — cancel the reload (we surface a toast instead of bouncing).
    clearWorkspaceSwitchFallback()
    const msg = err?.message || String(err) || 'unknown error'
    const timedOut = err?.code === ORG_SWITCH_TIMEOUT
    // B1/B2 (review of 65076304e): the deadline stops only the WAIT — the SDK
    // switch keeps running, and its late answer (or the server's
    // active-org-changed echo) would move this tab after any "still in your
    // previous organization" message; and the server may already hold the new
    // org/workspace. So a timeout always ends in ONE full reload: the boot reads
    // the server's truth, no late answer can land in this page, and the client
    // never rolls back alone. (The 12 s fallback is disarmed above.)
    if (timedOut && typeof window !== 'undefined' && window.location) {
      console.warn(
        '[AppOrgSwitcher] switch did not finish in time — reloading into the server state'
      )
      _end()
      window.location.reload()
      return
    }
    const notMember = err?.status === 403 || /not a member of target org/i.test(msg)
    // A network-class failure is transient — never permanently remove the org OR
    // workspace over a dropped connection; the user retries once they're back.
    const isNetwork =
      timedOut || /failed to fetch|network|unreachable|connection|load failed/i.test(msg)
    const extra = {}
    if (notMember) {
      const prev = Array.isArray(root?._invalidOrgs) ? root._invalidOrgs : []
      if (!prev.includes(org.id)) extra._invalidOrgs = [...prev, org.id]
    }
    // A genuine (non-network) failure to switch to THIS workspace means the row
    // only ever errors — drop it from the switcher (see _orgWorkspaces's filter),
    // exactly like a not-member org drops from the org list. Guarded to real
    // failures so a transient blip doesn't hide a good workspace.
    if (homeWorkspaceId && !isNetwork) {
      const prevW = Array.isArray(root?._invalidWorkspaces) ? root._invalidWorkspaces : []
      if (!prevW.includes(homeWorkspaceId)) extra._invalidWorkspaces = [...prevW, homeWorkspaceId]
    }
    _end(Object.keys(extra).length ? extra : undefined)
    console.warn('[AppOrgSwitcher] switch threw:', msg)
    // the toast's retry runs this same switch again (functions/retryOrgSwitch.js);
    // none for a membership refusal — a retry only meets the same 403 (N3)
    globalThis.__orgSwitchRetry = notMember
      ? null
      : () => _commitOrgSwitch(el, root, org, homeWorkspaceId, label)
    el.call('openNotification', {
      title: el
        .call('polyglot', 'shell.orgSwitcher.switchFailed')
        .replace(
          '{name}',
          label || org.name || el.call('polyglot', 'shell.orgSwitcher.workspaceFallback')
        ),
      // Sanitized for the user — a network-class failure otherwise shows the
      // SDK's developer-facing diagnostic (host, VPN/DNS/adblocker hints, the
      // raw "Failed to fetch"). `msg` above (raw) is still used for the
      // notMember regex check and the console.warn.
      message: userFacingErrorMessage(err, {
        networkMessage: el.call('polyglot', 'shell.orgSwitcher.networkError'),
        fallback: () => msg
      }),
      type: 'error',
      duration: 8000,
      ...(notMember
        ? {}
        : { action: { call: 'retryOrgSwitch' }, actionLabel: 'common.action.try_again' })
    })
  } finally {
    // whatever happened above, the switch flag never outlives the switch
    _end()
  }
}

// One org's workspace group inside level 2. `state` is an org; hidden until
// that org is drilled (shown imperatively by _slideOrgSwitcher).
export const AppWsGroup = {
  // FA204 / frank trap — see AppOrgSwitcher.scope's header comment above for
  // the full rationale. `orgWorkspaces` (+ its `workspaces` dependency) is
  // duplicated verbatim.
  scope: {
    workspaces(root) {
      const byOrg = root && root.orgWorkspaces
      if (!byOrg || typeof byOrg !== 'object') return []
      const out = []
      for (const k of Object.keys(byOrg)) {
        const list = byOrg[k]
        if (Array.isArray(list)) out.push(...list)
      }
      return out
    },
    orgWorkspaces(orgId, root) {
      const fetched = root && root.orgWorkspaces && root.orgWorkspaces[orgId]
      const base = Array.isArray(fetched)
        ? fetched
        : this.workspaces(root).filter((w) => (w.org_id || w.organization) === orgId)
      const invalid = root?._invalidWorkspaces
      if (Array.isArray(invalid) && invalid.length) {
        const skip = new Set(invalid)
        const filtered = base.filter((w) => !skip.has(w.id || w.workspace_id || w._id))
        return filtered.length ? filtered : base
      }
      return base
    }
  },
  attr: { 'data-ws-group': (el, s) => s.id },
  style: { display: 'none' },
  flow: 'y',

  Items: {
    flow: 'y',
    childExtends: 'AppWsItem',
    childrenAs: 'state',
    children: (el, s) => el.scope.orgWorkspaces(s.id, s.root)
  },
  Empty: {
    hide: (el, s) => el.scope.orgWorkspaces(s.id, s.root).length > 0,
    tag: 'p',
    margin: '0',
    // Match the L2 row padding/type so the empty-state label aligns with
    // the workspace rows it replaces.
    padding: 'Y Z1',
    fontSize: 'Z1',
    color: 'caption',
    text: '{{ shell.orgSwitcher.noWorkspaces | polyglot }}'
  },

  // "+ New workspace" — create a workspace in THIS org (`s` is the org), the L2
  // mirror of L1's "+ Create organization" footer (2026-06-14 per Nika). Gated
  // to org-management roles (the set AdminLink uses). Surfaced
  // optimistically into root.orgWorkspaces (the Mongo source the L2 lists
  // read) so the new workspace shows immediately without a round-trip.
  NewWs: {
    hide: (el, s) => {
      const r = s.role
      return !(r === 'owner' || r === 'co-owner' || r === 'admin' || r === 'maintainer')
    },
    tag: 'button',
    type: 'button',
    flow: 'x',
    align: 'center left',
    gap: 'Z1',
    width: '100%',
    marginTop: 'V',
    padding: 'Y Z1',
    round: 'radiusControl',
    borderWidth: '0',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 'Z1',
    fontWeight: '500',
    color: 'caption',
    textAlign: 'left',
    'aria-label': (el) => el.call('polyglot', 'shell.orgSwitcher.newWorkspaceAria'),
    // House MenuPanelItem hover (the `hover` wash token) plus a
    // caption→title brighten for the muted footer action.
    ':hover': {
      color: 'title',
      background: 'hover'
    },
    Icon: { extends: 'Icon', name: 'plus', boxSize: 'A', minWidth: 'A' },
    Label: { tag: 'span', text: '{{ shell.orgSwitcher.newWorkspace | polyglot }}' },
    onClick: (e, el, s) => {
      e.stopPropagation()
      const root = s.root
      const orgId = s.id
      el.call('promptDialog', {
        title: el.call('polyglot', 'shell.orgSwitcher.newWorkspace'),
        message: el
          .call('polyglot', 'shell.orgSwitcher.newWorkspacePrompt')
          .replace('{org}', s.name || el.call('polyglot', 'shell.orgSwitcher.orgFallback')),
        placeholder: el.call('polyglot', 'shell.orgSwitcher.newWorkspacePlaceholder'),
        confirmText: el.call('polyglot', 'common.action.create'),
        onConfirm: async (value) => {
          const displayName = (value || '').trim()
          if (!displayName) return
          try {
            const res = await el.sdk.createWorkspace({ organization: orgId, displayName })
            const ws = (res && (res.data || res.workspace || res)) || null
            const wid = ws && (ws._id || ws.id)
            if (wid) {
              // Surface the new workspace optimistically in root.orgWorkspaces
              // — the Mongo source the L2 lists read (_orgWorkspaces/_workspaces).
              // Normalized to id/name/org_id like the SDK prefetch so the row +
              // active-check + switch read it.
              const byOrg = { ...(root.orgWorkspaces || {}) }
              byOrg[orgId] = [
                ...(Array.isArray(byOrg[orgId]) ? byOrg[orgId] : []),
                {
                  id: wid,
                  workspace_id: wid,
                  org_id: orgId,
                  organization: orgId,
                  type: ws.type || 'shared',
                  displayName,
                  name: displayName
                }
              ]
              root.update({ orgWorkspaces: byOrg })
            }
          } catch (err) {
            console.warn(
              '[AppOrgSwitcher] createWorkspace failed:',
              err && err.message ? err.message : err
            )
            el.call('openNotification', {
              title: el.call('polyglot', 'shell.orgSwitcher.workspaceNotCreated'),
              message:
                (err && err.message) || el.call('polyglot', 'shell.orgSwitcher.createFailed'),
              type: 'warning'
            })
          }
        }
      })
    }
  }
}

// One workspace row (level 2). Clicking commits the switch to its org with
// this workspace as the home — the drill-down's commit point.
export const AppWsItem = {
  // FA204 / frank trap — see AppOrgSwitcher.scope's header comment above for
  // the full rationale. `activeWsId`/`orgs` (+ chain) are duplicated
  // verbatim for the RENDER-time reads below (background/aria-current/
  // Check.hide — everything that runs on mount, which is the surface this
  // sweep's acceptance bar (zero console errors on catalog page load)
  // actually covers).
  //
  // DELIBERATE, DOCUMENTED EXCEPTION — `onClick`'s call into `_commitOrgSwitch`
  // stays a bare module-scope reference, NOT `el.scope`-ified, unlike every
  // other call site in this file. It is NOT part of this file's emitted
  // `globalScope` surface (frank's emission gate names zero defects here), and
  // the commit flow only runs on a genuine interactive click — never on page
  // load or mount — needs a real SDK session, and is not exercised by a static
  // catalog sample.
  //
  // The original reason for the exception is now GONE and must not be quoted
  // back: it used to be that `_commitOrgSwitch` shared a module-scope
  // `_wsFallbackTimer` closure with the EXPORTED
  // `clearWorkspaceSwitchFallback`, which is imported and called
  // bare/argument-less by `packages/workspace/functions/loadPrivateData.js` (a
  // DIFFERENT package), so scope-ifying this one call would have orphaned that
  // timer. The timer now lives on `globalThis.__wsSwitchFallbackState`
  // (WS-SHARED-MODULE-LET-BLOCKS-EVERY-PUSH-1), which both packages reach
  // whatever the bundler does — so a future sweep MAY scope-ify this call.
  // That change needs its own live workspace-switch verification and is out of
  // this ticket's scope.
  scope: {
    mongoActiveOrgId(root) {
      const org = root && root.org
      return (org && (org.id || org._id)) || ''
    },
    rawOrgs(root) {
      // the switcher's list: functions/visibleOrgs.js (see `_rawOrgs`)
      const fromState = Array.isArray(root?.orgs) ? root.orgs : null
      return visibleOrgs(fromState || [], {
        keep: [root?.org?.id, root?.org?._id, root?.org?.slug, root?._pendingActiveOrgId]
      })
    },
    orgs(root) {
      const raw = this.rawOrgs(root)
      if (!raw.length) return []
      const invalid = root?._invalidOrgs
      if (Array.isArray(invalid) && invalid.length) {
        const skip = new Set(invalid)
        const filtered = raw.filter((o) => !skip.has(o.id))
        return filtered.length ? filtered : raw
      }
      return raw
    },
    activeWsId(root) {
      if (root?.workspace?.id) return String(root.workspace.id)
      try {
        const ls = getActiveWorkspacePointer()
        if (ls) return ls
      } catch (err) {
        console.warn('[AppWsItem] read activeWorkspace failed:', err?.message || err)
      }
      return ''
    }
  },
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center left',
  gap: 'Z1',
  width: '100%',
  // House context-dropdown row chrome (Y/Z1 padding, the control radius, Z1
  // fontSize, :hover) — matches AppOrgSwitcherItem so L1 and L2 rows read
  // identically; the active workspace wears the `selected` wash.
  padding: 'Y Z1',
  round: 'radiusControl',
  borderWidth: '0',
  background: (el, s) => {
    const a = el.scope.activeWsId(s.root)
    const w = s.id || s.workspace_id || s._id
    // undefined (not 'transparent') on non-active rows — see AppOrgSwitcherItem:
    // an inline transparent background defeats the :hover class wash below.
    return a && a === w ? 'selected' : undefined
  },
  cursor: 'pointer',
  fontSize: 'Z1',
  fontWeight: '500',
  color: 'title',
  textAlign: 'left',
  // Expose the active workspace to assistive tech alongside the checkmark.
  'aria-current': (el, s) => {
    const a = el.scope.activeWsId(s.root)
    const w = s.id || s.workspace_id || s._id
    return a && a === w ? 'true' : 'false'
  },
  // House MenuPanelItem :hover wash — the `hover` token as a flat prop (see
  // AppOrgSwitcherItem; a `:hover` block with only `@dark`/`@light` sub-blocks
  // emits no rule in css-in-props).
  ':hover': { background: 'hover' },

  Name: { tag: 'span', flex: '1', text: (el, s) => s.name },
  Role: {
    extends: 'AppMicroLabel',
    color: 'caption',
    onInit: async (el, s) => {
      if (s.role) return
      const wsId = s.id || s.workspace_id || s._id
      if (!wsId) {
        console.warn('[AppWsItem] workspace id not found')
        return
      }
      const sdk = el.sdk || el.context?.sdk || el.getRoot()?.sdk
      if (!sdk) {
        console.warn('[AppWsItem] sdk not found')
        return
      }
      try {
        const fetchPerms =
          typeof sdk.getWorkspacePermissions === 'function'
            ? (id) => sdk.getWorkspacePermissions(id)
            : typeof sdk.execute === 'function'
              ? (id) => sdk.execute('workspace', 'getWorkspacePermissions', id)
              : null
        if (!fetchPerms) {
          console.warn('[AppWsItem] cannot fetch permissions')
          return
        }
        const res = await fetchPerms(wsId)
        s.role = res?.role || res?.data?.role || (typeof res === 'string' ? res : null)
      } catch (err) {
        console.warn('[AppWsItem] getWorkspacePermissions failed:', err?.message || err)
      }
    },
    text: (el, s) => s.role || ''
  },
  // The current workspace: the check in ink (P5 — selection is monochrome).
  Check: {
    extends: 'Icon',
    name: 'check',
    boxSize: 'A',
    color: 'title',
    hide: (el, s) => {
      const a = el.scope.activeWsId(s.root)
      const w = s.id || s.workspace_id || s._id
      return !(a && a === w)
    }
  },

  onClick: (e, el, s) => {
    e.stopPropagation()
    const orgId = s.org_id || s.organization
    // The workspace's org is normally one of root.orgs (shared id-space), but
    // fall back to a minimal { id } so the switch still commits if it isn't —
    // _commitOrgSwitch only needs the org id, never the full record.
    const org = el.scope.orgs(s.root).find((o) => o.id === orgId) || (orgId ? { id: orgId } : null)
    const wsId = s.id || s.workspace_id || s._id
    // See the DELIBERATE, DOCUMENTED EXCEPTION comment on this component's
    // own `scope` above — _commitOrgSwitch stays a bare module reference.
    // Returned so a caller (the tests) can await the whole switch.
    return _commitOrgSwitch(el, s.root, org, wsId, s.name)
  }
}

// Full-viewport transition overlay shown while the org switch is in flight.
// Cleared by app.js's _onWorkspaceChange once all workspace-scoped data has
// been reloaded in-place (federated switches) or by the 400ms post-switch
// timer in onClick (non-federated). Driven by `root._wsSwitching`.
//
// Renders the brand's `MainLoader` (bare-key resolves via sharedLibraries →
// `@symbo-ls/brand/components/MainLoader.js`) so the switch shows the same
// app-init loader the rest of the product uses, plus a labelled overlay
// underneath identifying which workspace is being switched to.
// Org/workspace switch now shows the SAME clean full-screen brand MainLoader
// spiral (LoaderRatio + LogoLoader) that app-boot and workspace-project switch
// use — the shell-root MainLoader in workspace/app.js already mounts it for
// `_wsSwitching` (via _isLoaderActive). The old dark `black.85` scrim +
// "Switching to <org>…" label that stacked ON TOP of it was dropped (per Nika
// "use this loading when switching org/workspace") so every switch looks the
// same. Kept as a no-op mount so app.js's `AppOrgSwitchOverlay: {}` reference
// stays valid; `_wsSwitching` still drives the shell loader.
export const AppOrgSwitchOverlay = {
  display: 'none'
}
